import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Button, Field, Input } from '@tatra/ui';
import QRCode from 'qrcode';
import { type FormEvent, useEffect, useState } from 'react';
import { api, ApiError, errorMessage, get, post, setSession } from '../../lib/api.js';
import { AuthLayout } from './AuthLayout.js';

type Step =
  | { kind: 'password' }
  | { kind: 'verify'; mfaToken: string }
  | { kind: 'setup'; mfaToken: string; secret?: string; qr?: string }
  | { kind: 'codes'; codes: string[] };

interface SsoProviders {
  providers: { provider: 'GOOGLE' | 'MICROSOFT'; label: string }[];
  ssoRequired: boolean;
}

/** Why a single sign-on came back without a session (codes from the API's SSO callback). */
const SSO_ERRORS: Record<string, string> = {
  expired: 'That sign-in took too long or was finished in another browser. Please try again.',
  cancelled: 'Sign-in was cancelled at the provider.',
  provider_unavailable: 'The sign-in provider could not be reached. Try again in a minute.',
  failed: 'The provider’s answer could not be verified. Please try again, or ask your administrator.',
  domain_not_allowed: 'Use your work account: this account’s organisation or domain is not allowed here.',
  not_member: 'This account has not been invited to this organisation. Ask an administrator to invite you.',
  account_disabled: 'This account is disabled. Ask an administrator.',
  identity_mismatch:
    'Your CRM login is linked to a different account at this provider. Ask an administrator.',
  disabled: 'That sign-in method is turned off.',
};

function ProviderIcon({ provider }: { provider: 'GOOGLE' | 'MICROSOFT' }) {
  return provider === 'GOOGLE' ? (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.4 14.6 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12s4.3 9.6 9.6 9.6c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.6H12z"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#F25022" d="M2 2h9.5v9.5H2z" />
      <path fill="#7FBA00" d="M12.5 2H22v9.5h-9.5z" />
      <path fill="#00A4EF" d="M2 12.5h9.5V22H2z" />
      <path fill="#FFB900" d="M12.5 12.5H22V22h-9.5z" />
    </svg>
  );
}

interface SessionResponse {
  status: 'signed_in';
  accessToken: string;
  expiresIn: number;
  recoveryCodes?: string[];
}

export function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const search = useSearch({ strict: false }) as {
    redirect?: string;
    sso_result?: string;
    sso_error?: string;
  };
  const sso = useQuery({
    queryKey: ['sso-providers'],
    queryFn: () => get<SsoProviders>('/auth/sso/providers'),
    staleTime: 5 * 60_000,
  });
  const [step, setStep] = useState<Step>({ kind: 'password' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const finish = async (s: SessionResponse & { returnTo?: string | null }) => {
    setSession(s.accessToken, s.expiresIn);
    await qc.invalidateQueries({ queryKey: ['me'] });
    const wanted = s.returnTo ?? search.redirect;
    const target = wanted && wanted.startsWith('/') && !wanted.startsWith('//') ? wanted : '/';
    await navigate({ to: target });
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 429
          ? 'Too many attempts. Please wait a few minutes.'
          : errorMessage(e),
      );
    } finally {
      setBusy(false);
    }
  };

  const onPassword = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await post<
        SessionResponse | { status: 'mfa_required' | 'mfa_setup_required'; mfaToken: string }
      >('/auth/login', {
        email,
        password,
      });
      if (res.status === 'signed_in') return finish(res);
      setCode('');
      setStep(
        res.status === 'mfa_required'
          ? { kind: 'verify', mfaToken: res.mfaToken }
          : { kind: 'setup', mfaToken: res.mfaToken },
      );
    });
  };

  // Back from the provider: trade the one-time result code for a session (or the 2FA step).
  const [ssoDone, setSsoDone] = useState(false);
  useEffect(() => {
    if (ssoDone) return;
    if (search.sso_error) {
      setSsoDone(true);
      setError(SSO_ERRORS[search.sso_error] ?? 'Single sign-on did not work. Please try again.');
      return;
    }
    if (!search.sso_result) return;
    setSsoDone(true);
    const code = search.sso_result;
    // The code must not stay in the address bar or the history.
    window.history.replaceState(null, '', '/login');
    void run(async () => {
      const res = await api<
        | (SessionResponse & { returnTo: string | null })
        | { status: 'mfa_required' | 'mfa_setup_required'; mfaToken: string; returnTo: string | null }
      >('POST', '/auth/sso/result', { code }, { 'x-tatra-csrf': '1' });
      if (res.status === 'signed_in') return finish(res);
      setCode('');
      setStep(
        res.status === 'mfa_required'
          ? { kind: 'verify', mfaToken: res.mfaToken }
          : { kind: 'setup', mfaToken: res.mfaToken },
      );
    });
  }, [search.sso_result, search.sso_error, ssoDone]);

  // Start 2FA enrolment as soon as the setup step opens.
  useEffect(() => {
    if (step.kind !== 'setup' || step.secret) return;
    void run(async () => {
      const s = await post<{ secret: string; otpauthUrl: string }>('/auth/mfa/setup', {
        mfaToken: step.mfaToken,
      });
      const qr = await QRCode.toDataURL(s.otpauthUrl, { margin: 1, width: 200 });
      setStep({ ...step, secret: s.secret, qr });
    });
  }, [step]);

  const onVerify = (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'verify') return;
    void run(async () => {
      try {
        await finish(await post<SessionResponse>('/auth/mfa/verify', { mfaToken: step.mfaToken, code }));
      } catch (err) {
        // The 2FA step lapsed (e.g. a slow switch to the authenticator app): start again from the password.
        if (err instanceof ApiError && err.status === 401 && errorMessage(err).includes('expired')) {
          setCode('');
          setStep({ kind: 'password' });
        }
        throw err;
      }
    });
  };

  const onEnable = (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'setup') return;
    void run(async () => {
      const res = await post<SessionResponse>('/auth/mfa/enable', { mfaToken: step.mfaToken, code });
      setSession(res.accessToken, res.expiresIn);
      setStep({ kind: 'codes', codes: res.recoveryCodes ?? [] });
    });
  };

  if (step.kind === 'verify') {
    return (
      <AuthLayout
        title="Two-step verification"
        subtitle="Enter the 6-digit code from your authenticator app, or one of your recovery codes."
      >
        <form onSubmit={onVerify} className="space-y-4" noValidate>
          <Field
            label="Code"
            help="Codes change every 30 seconds — enter the one showing now."
            error={error ?? undefined}
          >
            {(p) => (
              <Input
                {...p}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="text"
                autoComplete="one-time-code"
                autoCapitalize="none"
                autoFocus
              />
            )}
          </Field>
          <Button type="submit" loading={busy} className="w-full">
            Verify
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => setStep({ kind: 'password' })}>
            Back
          </Button>
        </form>
      </AuthLayout>
    );
  }

  if (step.kind === 'setup') {
    return (
      <AuthLayout
        title="Set up two-step verification"
        subtitle="Your role needs a second step at sign-in. Scan this with Google Authenticator, Microsoft Authenticator or similar."
      >
        <form onSubmit={onEnable} className="space-y-4" noValidate>
          <div className="flex flex-col items-center gap-2">
            {step.qr ? (
              <img
                src={step.qr}
                alt="QR code for your authenticator app"
                className="h-48 w-48 rounded-md bg-white p-2"
              />
            ) : (
              <div className="h-48 w-48 animate-pulse rounded-md bg-surface-hover" />
            )}
            {step.secret && (
              <p className="text-center text-xs text-text-faint">
                Can’t scan? Enter this key: <span className="font-mono text-text-muted">{step.secret}</span>
              </p>
            )}
          </div>
          <Field label="6-digit code from the app" error={error ?? undefined}>
            {(p) => (
              <Input
                {...p}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            )}
          </Field>
          <Button type="submit" loading={busy} disabled={!step.secret} className="w-full">
            Turn on and continue
          </Button>
        </form>
      </AuthLayout>
    );
  }

  if (step.kind === 'codes') {
    return (
      <AuthLayout
        title="Save your recovery codes"
        subtitle="Each code works once if you lose your phone. Store them somewhere safe — they will not be shown again."
      >
        <ul className="grid grid-cols-2 gap-2 rounded-md border border-border bg-bg-elevated p-3 font-mono text-sm text-text">
          {step.codes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <div className="mt-4 flex gap-2">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => void navigator.clipboard?.writeText(step.codes.join('\n'))}
          >
            Copy
          </Button>
          <Button className="flex-1" onClick={() => void navigate({ to: '/' })}>
            I’ve saved them
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const providers = sso.data?.providers ?? [];
  const returnTo =
    search.redirect && search.redirect.startsWith('/') && !search.redirect.startsWith('//')
      ? `?returnTo=${encodeURIComponent(search.redirect)}`
      : '';
  return (
    <AuthLayout title="Sign in" subtitle="Use your work e-mail address.">
      {providers.length > 0 && (
        <div className="mb-5 space-y-2">
          {providers.map((p) => (
            <a
              key={p.provider}
              href={`/api/v1/auth/sso/${p.provider.toLowerCase()}/start${returnTo}`}
              className="flex min-h-11 w-full items-center justify-center gap-2 rounded-md border border-border bg-surface px-3.5 text-sm font-medium text-text hover:bg-surface-hover md:min-h-9"
            >
              <ProviderIcon provider={p.provider} />
              Continue with {p.label}
            </a>
          ))}
          {sso.data?.ssoRequired && (
            <p className="text-xs text-text-faint">
              Your organisation signs in with the button above. The password form is for administrators only.
            </p>
          )}
          <div className="flex items-center gap-3 pt-2 text-xs text-text-faint" aria-hidden="true">
            <span className="h-px flex-1 bg-border" />
            or with a password
            <span className="h-px flex-1 bg-border" />
          </div>
        </div>
      )}
      <form onSubmit={onPassword} className="space-y-4" noValidate>
        <Field label="E-mail">
          {(p) => (
            <Input
              {...p}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          )}
        </Field>
        <Field label="Password" error={error ?? undefined}>
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
        </Field>
        <Button type="submit" loading={busy} className="w-full">
          Sign in
        </Button>
        <p className="text-center text-sm">
          <Link to="/forgot-password" className="text-primary hover:underline">
            Forgot your password?
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
